import { SECTION_HREF_MAP as CLOSING_SECTION_HREF_MAP } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/EditDrawer.hrefs';
import { ClosingSubsectionNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { LoanApplicationTabRouteParam } from 'constants/loanApplication';

import {
  CLOSING_FUNDING_DATES_FIELD_NAMES,
  CLOSING_SUBSECTION_TO_SECTION_HREF,
  FIELD_HIGHLIGHT_INTERVAL_MS,
  FIELD_HIGHLIGHT_MAX_ATTEMPTS,
  IN_PAGE_EDITOR_OPENER_MAX_ATTEMPTS,
  POST_CLOSING_TAB_TO_SECTION_HREF,
  SECTION_TO_TAB,
  SUBSECTION_TO_TAB,
} from './loanNavigation.constants';

const NAV_LOG_PREFIX = '[ai-chat-nav]';

export const logNav = (event, payload) => {
  console.info(`${NAV_LOG_PREFIX} ${event}`, payload);
};

export const delay = ms =>
  new Promise(resolve => {
    const start = performance.now();

    const checkTime = () => {
      if (performance.now() - start >= ms) {
        resolve();

        return;
      }

      requestAnimationFrame(checkTime);
    };

    requestAnimationFrame(checkTime);
  });

export const getFieldLeaf = fieldId => fieldId.split('-').pop();

// With the restyled Underwriting tab on, the nav holds `underwriting-new` instead of
// `underwriting`. Callers read the toggle and pass it in the navigation context, and
// every route is resolved against it here (LEN2-499).
export const resolveTabRoute = (tabRoute, { isRestyledUnderwritingTabEnabled = false } = {}) =>
  tabRoute === LoanApplicationTabRouteParam.UNDERWRITING && isRestyledUnderwritingTabEnabled
    ? LoanApplicationTabRouteParam.UNDERWRITING_NEW
    : tabRoute;

// The loan tab an area lives on — by subsection first (see SUBSECTION_TO_TAB), then by
// section. Null for areas no loan screen holds (e.g. Lead details).
export const getAreaTabRoute = ({ section, subsection, context }) =>
  resolveTabRoute(SUBSECTION_TO_TAB[subsection] ?? SECTION_TO_TAB[section] ?? null, context);

// The Closing edit drawer section holding a Closing field — the Closing Information
// form spans two drawer sections (LEN2-499).
const getClosingSectionHref = ({ subsection, fieldId }) => {
  const isClosingFundingDatesField =
    subsection === ClosingSubsectionNames.CLOSING_INFORMATION &&
    CLOSING_FUNDING_DATES_FIELD_NAMES.includes(getFieldLeaf(fieldId));

  if (isClosingFundingDatesField) {
    return CLOSING_SECTION_HREF_MAP.CLOSING_FUNDING_DATES;
  }

  return CLOSING_SUBSECTION_TO_SECTION_HREF[subsection] ?? null;
};

// The tab + drawer section (`<tab>/<sectionHref>`) a Closing-section field is edited in:
// Post Closing has its own tab and drawer. Null when no drawer section holds it.
export const getClosingDrawerRoute = ({ subsection, tab, fieldId }) => {
  if (subsection === ClosingSubsectionNames.POST_CLOSING) {
    const postClosingSectionHref = POST_CLOSING_TAB_TO_SECTION_HREF[tab];

    return _.isNil(postClosingSectionHref)
      ? null
      : { tabRoute: LoanApplicationTabRouteParam.POST_CLOSING, subtab: postClosingSectionHref };
  }

  const closingSectionHref = getClosingSectionHref({ subsection, fieldId });

  return _.isNil(closingSectionHref)
    ? null
    : { tabRoute: LoanApplicationTabRouteParam.CLOSING_NEW, subtab: closingSectionHref };
};

const isElementEnabled = element =>
  !element.disabled && element.getAttribute('aria-disabled') !== 'true';

const findOpenerStepElement = ({ selector, text }) =>
  Array.from(document.querySelectorAll(selector)).find(
    element => isElementEnabled(element) && (_.isNil(text) || element.textContent.trim() === text),
  ) ?? null;

const waitForOpenerStepElement = async (step, attemptsRemaining = FIELD_HIGHLIGHT_MAX_ATTEMPTS) => {
  const element = findOpenerStepElement(step);

  if (element || attemptsRemaining <= 1) {
    return element;
  }

  await delay(FIELD_HIGHLIGHT_INTERVAL_MS);

  return waitForOpenerStepElement(step, attemptsRemaining - 1);
};

// The first opener whose first step is clickable — retried while the page loads.
const waitForOpener = async (openers, attemptsRemaining = IN_PAGE_EDITOR_OPENER_MAX_ATTEMPTS) => {
  const opener = openers.find(([firstStep]) => findOpenerStepElement(firstStep)) ?? null;

  if (opener || attemptsRemaining <= 1) {
    return opener;
  }

  await delay(FIELD_HIGHLIGHT_INTERVAL_MS);

  return waitForOpener(openers, attemptsRemaining - 1);
};

const runOpenerSteps = async ([step, ...nextSteps]) => {
  const element = await waitForOpenerStepElement(step);

  if (!element) {
    logNav('openInPageEditor:step-not-found', { step });

    return;
  }

  element.click();

  if (!_.isEmpty(nextSteps)) {
    await runOpenerSteps(nextSteps);
  }
};

// Clicks the button(s) that turn an in-place section into its editor (LEN2-499). When no
// opener is clickable (already editing, no permission, section hidden) navigation still
// lands on the page and highlights whatever the field selectors find.
export const openInPageEditor = async openers => {
  if (_.isEmpty(openers)) {
    return;
  }

  const opener = await waitForOpener(openers);

  if (!opener) {
    logNav('openInPageEditor:no-opener', { openers });

    return;
  }

  await runOpenerSteps(opener);
};
